// frontend/src/pages/BrandVoicePage.jsx
import React, { useState, useEffect } from 'react';
import { getBrandVoices, createBrandVoice, updateBrandVoice, deleteBrandVoice, extractBrandVoice } from '../services/api';
import { Mic, RefreshCw, Save, Loader, CheckCircle, XCircle, ChevronDown, ChevronUp, Plus, Trash2 } from 'lucide-react';

const BrandVoicePage = () => {
  const [voices, setVoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);
  const [extracting, setExtracting] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [expandedVoice, setExpandedVoice] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [showAddForm, setShowAddForm] = useState(false);
  const [newVoice, setNewVoice] = useState({ name: '', description: '' });
  const [addingVoice, setAddingVoice] = useState(false);

  useEffect(() => {
    loadVoices();
  }, []);

  const loadVoices = async () => {
    try {
      const res = await getBrandVoices();
      setVoices(res.data);
    } catch (err) {
      console.error('Failed to load brand voices', err);
    } finally {
      setLoading(false);
    }
  };

  const handleExpand = (voice) => {
    if (expandedVoice === voice.id) {
      setExpandedVoice(null);
    } else {
      setExpandedVoice(voice.id);
      setEditForm({
        description: voice.description || '',
        sample_articles: (voice.sample_articles || []).join('\n\n---ARTICLE SEPARATOR---\n\n')
      });
    }
  };

  const handleToggleActive = async (voice) => {
    const newStatus = voice.is_active === 'false' ? 'true' : 'false';
    try {
      await updateBrandVoice(voice.id, { is_active: newStatus });
      setVoices(voices.map(v => v.id === voice.id ? { ...v, is_active: newStatus } : v));
      setSuccess('"' + voice.name + '" ' + (newStatus === 'true' ? 'activated' : 'deactivated'));
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to update brand voice');
    }
  };

  const handleSave = async (voiceId) => {
    setSaving(voiceId);
    setSuccess('');
    setError('');
    try {
      const articles = editForm.sample_articles
        .split('---ARTICLE SEPARATOR---')
        .map((a) => a.trim())
        .filter(Boolean);
      await updateBrandVoice(voiceId, {
        description: editForm.description,
        sample_articles: articles
      });
      setSuccess('Brand voice updated successfully');
      setTimeout(() => setSuccess(''), 3000);
      loadVoices();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to update brand voice');
    } finally {
      setSaving(null);
    }
  };

  const handleExtract = async (voiceId) => {
    setExtracting(voiceId);
    setSuccess('');
    setError('');
    try {
      await extractBrandVoice(voiceId);
      setSuccess('Brand voice extraction started. This may take a minute...');
      setTimeout(() => { loadVoices(); setSuccess(''); }, 10000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to start extraction');
    } finally {
      setExtracting(null);
    }
  };

  const handleDelete = async (voice) => {
    if (!window.confirm('Delete "' + voice.name + '"? This cannot be undone.')) return;
    setDeleting(voice.id);
    setError('');
    try {
      await deleteBrandVoice(voice.id);
      setVoices(voices.filter(v => v.id !== voice.id));
      setSuccess('"' + voice.name + '" deleted');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to delete brand voice');
    } finally {
      setDeleting(null);
    }
  };

  const handleAddVoice = async () => {
    if (!newVoice.name.trim()) {
      setError('Name is required');
      return;
    }
    setAddingVoice(true);
    setError('');
    try {
      const res = await createBrandVoice({
        name: newVoice.name.trim(),
        description: newVoice.description.trim(),
        sample_articles: [],
        is_active: 'true'
      });
      setVoices([...voices, res.data]);
      setNewVoice({ name: '', description: '' });
      setShowAddForm(false);
      setSuccess('"' + res.data.name + '" created successfully');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create brand voice');
    } finally {
      setAddingVoice(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader className="animate-spin text-brand-mid" size={32} />
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-brand-dark">Brand Voice</h1>
          <p className="text-gray-500 mt-1">Manage voice profiles that shape how content is written</p>
        </div>
        <button
          onClick={() => { setShowAddForm(true); setError(''); }}
          className="flex items-center gap-2 px-4 py-2 bg-brand-dark text-white rounded-lg hover:bg-brand-mid transition-colors cursor-pointer"
        >
          <Plus size={18} />
          Add Brand Voice
        </button>
      </div>

      {/* Messages */}
      {success && (
        <div className="bg-green-50 text-green-700 p-4 rounded-lg mb-6 flex items-center gap-2">
          <CheckCircle size={18} />
          {success}
        </div>
      )}
      {error && (
        <div className="bg-red-50 text-red-600 p-4 rounded-lg mb-6 flex items-center gap-2">
          <XCircle size={18} />
          {error}
        </div>
      )}

      {/* How It Works */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6">
        <p className="text-sm text-blue-700">
          <strong>How Brand Voice works:</strong> Each voice profile shapes how content is written.
          Active voices appear as options when running content generation.
          Upload sample articles and click "Extract Profile" to train each voice on your writing style.
          Inactive voices are hidden from run options but their data is preserved.
        </p>
      </div>

      {/* Add New Voice Form */}
      {showAddForm && (
        <div className="bg-white rounded-xl shadow-sm border border-brand-mid p-5 mb-6">
          <h3 className="text-md font-semibold text-brand-dark mb-4">New Brand Voice</h3>
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={newVoice.name}
                onChange={(e) => setNewVoice({ ...newVoice, name: e.target.value })}
                placeholder="e.g. Client ABC — Thought Leadership"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-mid text-sm"
              />
              <p className="text-xs text-gray-400 mt-1">
                This becomes the identifier used in content runs — keep it clear and unique.
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <input
                type="text"
                value={newVoice.description}
                onChange={(e) => setNewVoice({ ...newVoice, description: e.target.value })}
                placeholder="e.g. Practical focus on governance and capacity building"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-mid text-sm"
              />
            </div>
            <div className="flex gap-3 pt-1">
              <button
                onClick={handleAddVoice}
                disabled={addingVoice}
                className="flex items-center gap-2 px-4 py-2 bg-brand-dark text-white rounded-lg hover:bg-brand-mid disabled:opacity-50 cursor-pointer transition-colors"
              >
                <Plus size={16} />
                {addingVoice ? 'Creating...' : 'Create Brand Voice'}
              </button>
              <button
                onClick={() => { setShowAddForm(false); setNewVoice({ name: '', description: '' }); setError(''); }}
                className="px-4 py-2 border border-gray-300 text-gray-600 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Voice Profiles */}
      {voices.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
          <Mic size={40} className="mx-auto mb-4 text-gray-300" />
          <p className="text-gray-500">No brand voice profiles found. Add one to get started.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {voices.map((voice) => (
            <div
              key={voice.id}
              className={`bg-white rounded-xl shadow-sm border border-gray-200 transition-opacity ${voice.is_active === 'false' ? 'opacity-60' : ''}`}
            >
              {/* Voice Header */}
              <div className="p-5 flex items-center justify-between">
                <div
                  className="flex items-center gap-3 flex-1 cursor-pointer"
                  onClick={() => handleExpand(voice)}
                >
                  <Mic size={20} className={voice.is_active === 'false' ? 'text-gray-300' : 'text-brand-mid'} />
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="text-md font-semibold text-gray-800">{voice.name}</h3>
                      {voice.is_active === 'false' && (
                        <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-400 rounded-full">Inactive</span>
                      )}
                    </div>
                    <p className="text-sm text-gray-500">{voice.description}</p>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <span className="text-xs text-gray-400">{voice.sample_articles?.length || 0} samples</span>
                  <span className={`text-xs px-2 py-1 rounded-full ${
                    voice.extracted_profile?.raw_profile
                      ? 'bg-green-100 text-green-700'
                      : 'bg-yellow-100 text-yellow-700'
                  }`}>
                    {voice.extracted_profile?.raw_profile ? 'Profile Extracted' : 'Needs Extraction'}
                  </span>

                  {/* Active/Inactive Toggle */}
                  <label
                    className="relative inline-flex items-center cursor-pointer"
                    title={voice.is_active === 'false' ? 'Click to activate' : 'Click to deactivate'}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <input
                      type="checkbox"
                      checked={voice.is_active !== 'false'}
                      onChange={() => handleToggleActive(voice)}
                      className="sr-only peer"
                    />
                    <div className="w-9 h-5 bg-gray-200 rounded-full peer peer-checked:bg-brand-mid relative after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border after:border-gray-300 after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:after:translate-x-4"></div>
                  </label>

                  {/* Delete */}
                  <button
                    onClick={(e) => { e.stopPropagation(); handleDelete(voice); }}
                    disabled={deleting === voice.id || voices.length <= 1}
                    title={voices.length <= 1 ? 'Cannot delete the last brand voice' : 'Delete brand voice'}
                    className="p-1.5 text-gray-300 hover:text-red-500 disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer transition-colors"
                  >
                    <Trash2 size={16} />
                  </button>

                  <button onClick={() => handleExpand(voice)} className="p-1.5 text-gray-400 cursor-pointer">
                    {expandedVoice === voice.id ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                  </button>
                </div>
              </div>

              {/* Expanded Content */}
              {expandedVoice === voice.id && (
                <div className="border-t border-gray-200 p-5">
                  {voice.extracted_profile?.raw_profile && (
                    <div className="mb-6">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Current Extracted Profile
                      </label>
                      <div className="bg-gray-50 p-4 rounded-lg text-sm text-gray-600 whitespace-pre-wrap max-h-64 overflow-y-auto">
                        {voice.extracted_profile.raw_profile}
                      </div>
                      {voice.extracted_profile.num_articles_analysed > 0 && (
                        <p className="text-xs text-gray-400 mt-2">
                          Based on {voice.extracted_profile.num_articles_analysed} articles
                        </p>
                      )}
                    </div>
                  )}

                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                    <input
                      type="text"
                      value={editForm.description || ''}
                      onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-mid text-sm"
                    />
                  </div>

                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Sample Articles</label>
                    <p className="text-xs text-gray-500 mb-2">
                      Paste your sample articles below. Separate each article with a line containing only: ---ARTICLE SEPARATOR---
                    </p>
                    <textarea
                      value={editForm.sample_articles || ''}
                      onChange={(e) => setEditForm({ ...editForm, sample_articles: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-mid text-sm font-mono"
                      rows={10}
                      placeholder="Paste your first article here...&#10;&#10;---ARTICLE SEPARATOR---&#10;&#10;Paste your second article here..."
                    />
                  </div>

                  <div className="flex justify-end gap-3">
                    <button
                      onClick={() => handleExtract(voice.id)}
                      disabled={extracting === voice.id}
                      className="flex items-center gap-2 px-4 py-2 border border-brand-mid text-brand-mid rounded-lg hover:bg-brand-light disabled:opacity-50 cursor-pointer transition-colors"
                    >
                      <RefreshCw size={16} className={extracting === voice.id ? 'animate-spin' : ''} />
                      {extracting === voice.id ? 'Extracting...' : 'Extract Profile'}
                    </button>
                    <button
                      onClick={() => handleSave(voice.id)}
                      disabled={saving === voice.id}
                      className="flex items-center gap-2 px-4 py-2 bg-brand-dark text-white rounded-lg hover:bg-brand-mid disabled:opacity-50 cursor-pointer transition-colors"
                    >
                      <Save size={16} />
                      {saving === voice.id ? 'Saving...' : 'Save Changes'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default BrandVoicePage;